// @flow
// TODO(v1-removal): The bankAdvisor branch in this component is unreachable after V1 routes redirect to V2. Remove the bankAdvisor logic and related imports (getBankAdvisorInquiries, bankAdvisorInitialPayload, tabsBankAdvisor, mapResponseForBankAdvisor, getTableDataForBankAdvisor, getTableHeaders) after grace period. The customer branch remains active.

import * as React from "react";
import { withRouter } from "react-router-dom";

import { PageHeader, Table, Tabs, FlashMessageConsumer, Preloader } from "buba-components";
import PageLayout, { PageLayoutScrollContext } from "buba-components/src/components/PageLayout";
import DataFetcher from "buba-components/src/components/DataFetcher";
import snakeCase from "lodash/snakeCase";
import isNull from "lodash/isNull";
import {
  getInquiryListParams,
  saveInquiryListParams,
  clearInquiryListParams
} from "buba-components/src/utils/inquiryListParams";
import styles from "./InquiryList.scss";
import paths from "../../data/paths";
import { getBankAdvisorInquiries, getBankOffers } from "../../api/actions";
import { bankAdvisorInitialPayload, customerInitialPayload } from "./payload";
import { getTableHeaders } from "./tableHeaders";
import { mapResponseForBankAdvisor, mapResponseForCustomer } from "./mappers";
import { getTableDataForBankAdvisor, getTableDataForCustomer } from "./tableData";
import { tabsBankAdvisor, tabsCustomer } from "./tabs";

export type ListType = "customer" | "bankAdvisor";

type InquiriesListProps = {
  history: {
    push: string => void
  },
  type: ListType
};

export const isCustomerInquiry = (type: ListType) => type === "customer";

const InquiriesList = ({ history, type }: InquiriesListProps) => {
  const [sortedBy, setSortedBy] = React.useState("created_at");
  const [sortingDirection, setSortingDirection] = React.useState("desc");
  const isCustomerTab = isCustomerInquiry(type);
  const initialPayload = isCustomerTab ? customerInitialPayload : bankAdvisorInitialPayload;

  const createInitialPayload = () => {
    const inquiryListParams = getInquiryListParams();

    const isSameInquiryType = isNull(inquiryListParams)
      ? false
      : history.location.pathname.includes(inquiryListParams.currentLocation);

    if (isSameInquiryType) {
      return {
        ...initialPayload,
        ...inquiryListParams,
        creator: snakeCase(type)
      };
    }

    clearInquiryListParams();

    return {
      ...initialPayload,
      creator: snakeCase(type)
    };
  };

  const initialPayloadSource = createInitialPayload();

  return (
    <PageLayout>
      <PageHeader header="Übersicht" bottomMargin />
      <FlashMessageConsumer>
        {({ addMessage }) => (
          <PageLayoutScrollContext.Consumer>
            {({ scrollToTop }) => (
              <DataFetcher
                initialPayload={initialPayloadSource}
                mapper={isCustomerTab ? mapResponseForCustomer : mapResponseForBankAdvisor}
                onError={msg => addMessage(msg, "failure")}
                apiAction={isCustomerTab ? getBankOffers : getBankAdvisorInquiries}
              >
                {({ data, isLoading, requestFetch }) => {
                  if (!data) {
                    return null;
                  }

                  const { inquiries, pagination, tabCounts } = data;

                  const requestPageAndScroll = (page: number | null) => {
                    if (!page) {
                      return;
                    }

                    requestFetch({ page });
                    scrollToTop();
                  };

                  const requestSortingAndScroll = (columnId: string, direction: "asc" | "desc") => {
                    requestFetch({
                      sortBy: columnId,
                      sortDirection: direction
                    });

                    setSortedBy(columnId);
                    setSortingDirection(direction);
                  };

                  const tabs = isCustomerTab ? tabsCustomer : tabsBankAdvisor;

                  const dashboardPath = isCustomerTab
                    ? paths.customerInquiryDashboard
                    : paths.bankInquiryDashboard;

                  saveInquiryListParams({
                    page: pagination.current,
                    currentLocation: history.location.pathname
                  });

                  return (
                    <Tabs
                      initialActiveIndex={initialPayloadSource.initialActiveIndex}
                      onChange={index => {
                        requestFetch({ status: tabs[index].id, page: 1 });
                        saveInquiryListParams({
                          status: tabs[index].id,
                          initialActiveIndex: index
                        });
                      }}
                      tabs={tabs.map(tab => ({
                        title: () => tab.title,
                        content: () => {
                          switch (true) {
                            case isLoading:
                              return (
                                <div className={styles.placeholder}>
                                  <Preloader />
                                </div>
                              );
                            case inquiries.length === 0:
                              return (
                                <div className={styles.placeholder}>
                                  Es sind keine Anfragen mit diesem Status verfügbar.
                                </div>
                              );
                            default:
                              return (
                                <Table
                                  tableHeaders={getTableHeaders(
                                    requestSortingAndScroll,
                                    type,
                                    tab.id
                                  )}
                                  data={
                                    isCustomerTab
                                      ? getTableDataForCustomer(inquiries, tab.id)
                                      : getTableDataForBankAdvisor(inquiries)
                                  }
                                  maxPages={pagination.totalPages}
                                  site="bank"
                                  onInquiryClick={id => history.push(dashboardPath(id))}
                                  currentPage={pagination.current}
                                  getNextPage={() => requestPageAndScroll(pagination.next)}
                                  getPreviousPage={() => requestPageAndScroll(pagination.prev)}
                                  sortedBy={sortedBy}
                                  sortingDirection={sortingDirection}
                                />
                              );
                          }
                        },
                        amount: tabCounts[tab.id]
                      }))}
                    />
                  );
                }}
              </DataFetcher>
            )}
          </PageLayoutScrollContext.Consumer>
        )}
      </FlashMessageConsumer>
    </PageLayout>
  );
};

export default withRouter(InquiriesList);
